import {
  Accordion,
  Badge,
  Box,
  createStyles,
  Grid,
  Flex,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useIsXs } from './hooks';
import { CompareListItem } from './CompareListItem';

// CompareList
const CompareList = (props: {
  items: {
    values: React.ComponentProps<typeof CompareListItem>[];
    badge: any;
    uuid: string;
  }[];
  title: React.ReactNode;
  headerTooltip: React.ReactNode;
  subtitle: React.ReactNode;
  description: React.ReactNode;
  listHeader: any[];
  showDetails: boolean;
  useFilledCompareItems: boolean;
}) => {
  const {
    title,
    headerTooltip,
    subtitle,
    description,
    listHeader,
    items,
    showDetails,
    useFilledCompareItems,
    ...rest
  } = props;

  const renderData = listHeader.map((headline, index) => ({
    ...items[index],
    uuid: uuid(),
    headline,
    values: items.map((item) => ({
      ...item.values[index],
      uuid: uuid(),
    })),
  }));

  const [value, setValue] = useState(showDetails ? 'showDetails' : null);

  const HeaderRenderer = useMemo(
    () =>
      !headerTooltip
        ? ({ children }) => children
        : ({ children }) => (
            <Tooltip position="bottom-start" offset={0} label={headerTooltip}>
              {children}
            </Tooltip>
          ),
    [headerTooltip],
  );
  const isXs = useIsXs();

  const BadgeRenderer = useMemo(
    () =>
      isXs
        ? ({ text, children }) => (
            <Tooltip label={text.children || text}>{children}</Tooltip>
          )
        : ({ text, children }) => children,
    [isXs],
  );

  return (
    <Box {...rest}>
      <HeaderRenderer>
        <Box>
          <Title order={3} size="h4">
            {title}
          </Title>
          <Text mb="sm" size="sm">
            {description}
          </Text>
        </Box>
      </HeaderRenderer>

      {items.some((item) => item.badge) && (
        <Grid gutter={2} gutterSm="xs" gutterMd="xl" columns={items.length}>
          {items.map((item) => {
            return (
              <Grid.Col span={1} key={item.uuid}>
                {item.badge && (
                  <BadgeRenderer text={item.badge}>
                    <Badge
                      {...{
                        children:
                          typeof item.badge === 'string'
                            ? item?.badge
                            : isXs && item?.badge?.smallChildren
                            ? item?.badge?.smallChildren
                            : item?.badge?.children,
                        color: item?.badge?.color,
                        styles: item?.badge?.styles,
                      }}
                      w="100%"
                    />
                  </BadgeRenderer>
                )}
              </Grid.Col>
            );
          })}
        </Grid>
      )}
      <Accordion
        unstyled
        value={value}
        onChange={setValue}
        mt="sm"
        chevron={null}
      >
        <Accordion.Item value="showDetails">
          <Accordion.Panel>
            <Title order={6} mb="sm">
              {subtitle}
            </Title>
            {renderData.map((entry) => {
              return (
                <div key={entry.uuid}>
                  <Title order={6}>{entry.headline}</Title>
                  <Grid
                    columns={items.length}
                    gutter={0}
                    mb="sm"
                    sx={(theme) => ({
                      border: `1px solid ${theme.colors.fgMuted[2]}`,
                      borderLeft: 'none',
                    })}
                  >
                    {entry.values.map(({ uuid, ...v }) => {
                      return (
                        <Grid.Col
                          key={uuid}
                          span={1}
                          sx={(theme) => ({
                            borderLeft: `1px solid ${theme.colors.fgMuted[2]}`,
                            height: 'auto',
                          })}
                        >
                          <CompareListItem
                            {...v}
                            useFilledCompareItem={useFilledCompareItems}
                          />
                        </Grid.Col>
                      );
                    })}
                  </Grid>
                </div>
              );
            })}
          </Accordion.Panel>
          <Accordion.Control
            chevron={null}
            c="primary"
            fz="md"
            w="100%"
            sx={(theme) => ({
              background: theme.colors.accentSubtle[0],
              border: 'none',
              gap: 6,
              padding: '8px 0px',
              borderRadius: 6,
              cursor: 'pointer',
              '& *[class*="-Accordion-chevron"]': {
                display: 'none',
              },
            })}
          >
            {value == null ? 'Details anzeigen' : 'Details verbergen'}
          </Accordion.Control>
        </Accordion.Item>
      </Accordion>
    </Box>
  );
};

CompareList.propTypes = {
  title: PropTypes.string,
  headerTooltip: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  listHeader: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(PropTypes.shape(Badge.propTypes)),
  showDetails: PropTypes.bool,
};

CompareList.defaultProps = {
  title: null,
  subtitle: null,
  description: null,
  listHeader: [],
  items: [],
  showDetails: false,
  headerTooltip: null,
};

export default CompareList;
